import { mean, stdDev } from '../geometry/statistics.js';
import { clamp } from '../types/geometry.js';
import type { CheckRunContext } from '../types/checks.js';
import type { AuthenticitySignal } from './index.js';

/**
 * Signals A (compression history), C (resampling) and H (clone / copy-paste).
 *
 * All three are deliberately conservative heuristics running on the analysis
 * buffer only: no model, no randomness, no timers. When a signal cannot be
 * measured honestly on this image it returns a near-zero confidence, which the
 * ensemble reports as `unavailable` instead of inventing a score.
 */

const JPEG_MIMES = new Set(['image/jpeg', 'image/jpg']);

const clamp01 = (value: number): number => clamp(value, 0, 1);

// ---------------------------------------------------------------------------
// A — compression history
// ---------------------------------------------------------------------------

/**
 * Reads the deep-quality compression metrics together with the file economics.
 *
 * A re-saved or double-compressed JPEG is usually visible in two independent
 * ways: the 8×8 blockiness / ringing measured on the pixel grid, and the number
 * of bytes the file spends per pixel (a very low value means aggressive
 * quantization; a very high value in a JPEG container hints at a re-save or a
 * re-encode from a lossless source).
 */
export const compressionHistorySignal = (ctx: CheckRunContext): AuthenticitySignal => {
  const compression = ctx.quality?.compression;
  const sizeBytes = ctx.fileInfo.sizeBytes ?? ctx.image.sizeBytes ?? 0;
  const pixelTotal = ctx.image.width * ctx.image.height;
  const hasBpp = sizeBytes > 0 && pixelTotal > 0;
  const bytesPerPixel = hasBpp ? sizeBytes / pixelTotal : 0;
  const mime = (ctx.fileInfo.detectedMimeType ?? ctx.image.mimeType ?? '').toLowerCase();
  const isJpeg = JPEG_MIMES.has(mime);

  if (compression === undefined && !hasBpp) {
    return {
      id: 'compression_history',
      score: 0,
      confidence: 0.02,
      note: 'Нет данных о сжатии: метрики качества и размер файла недоступны.',
    };
  }

  const blockiness = compression?.measured ? compression.blockiness : 0;
  const ringing = compression?.measured ? compression.ringing : 0;

  const blockTerm = clamp01(blockiness / 0.12);
  const ringTerm = clamp01(ringing / 0.35);

  let bppTerm = 0;
  if (hasBpp) {
    if (bytesPerPixel < 0.12) {
      bppTerm = clamp01((0.12 - bytesPerPixel) / 0.09);
    } else if (bytesPerPixel > 1.4) {
      // A JPEG that spends more than ~1.4 bytes per pixel looks re-saved or
      // converted rather than camera-native.
      bppTerm = 0.35;
    }
  }

  const score = clamp01(0.5 * blockTerm + 0.3 * ringTerm + 0.3 * bppTerm);

  const reasons: string[] = [];
  if (blockTerm >= 0.5) reasons.push('заметна блочность JPEG (сетка 8×8)');
  if (ringTerm >= 0.5) reasons.push('«звон» вокруг контуров');
  if (bppTerm >= 0.4 && bytesPerPixel < 0.12) {
    reasons.push('очень мало данных на пиксель — сильная переупаковка');
  }
  if (bppTerm >= 0.3 && bytesPerPixel > 1.4) {
    reasons.push('нехарактерно большой объём данных для JPEG');
  }

  let confidence = 0.45;
  if (compression?.measured) confidence += 0.25;
  if (hasBpp) confidence += 0.1;
  if (!isJpeg) confidence -= 0.15;
  confidence = clamp01(confidence);

  return {
    id: 'compression_history',
    score,
    confidence,
    note:
      reasons.length > 0
        ? `следы повторного сжатия: ${reasons.join(', ')}`
        : 'признаков повторного сжатия не видно',
  };
};

// ---------------------------------------------------------------------------
// C — resampling
// ---------------------------------------------------------------------------

/**
 * Measures 2× block repetition: a nearest-neighbour or cheap resample makes
 * every second sample a duplicate of its neighbour, so the difference *inside*
 * a 2×2 cell drops while the difference to the next cell stays.
 *
 * The anchor parity is unknown (the resampling grid may start on an odd pixel),
 * so both parities are measured and the stronger one is kept.
 */
const blockRepetitionScore = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
): number => {
  let best = 0;
  for (const offset of [0, 1]) {
    let within = 0;
    let withinCount = 0;
    let next = 0;
    let nextCount = 0;
    for (let y = offset; y + 2 < height; y += 2) {
      for (let x = offset; x + 2 < width; x += 2) {
        const c = luma[y * width + x] as number;
        const right = luma[y * width + x + 1] as number;
        const down = luma[(y + 1) * width + x] as number;
        const right2 = luma[y * width + x + 2] as number;
        const down2 = luma[(y + 2) * width + x] as number;
        within += Math.abs(c - right) + Math.abs(c - down);
        withinCount += 2;
        next += Math.abs(right - right2) + Math.abs(down - down2);
        nextCount += 2;
      }
    }
    if (withinCount === 0 || nextCount === 0) continue;
    const inside = within / withinCount;
    const outside = next / nextCount;
    const score = clamp01(((outside - inside) / (outside + inside + 1e-6)) * 2);
    if (score > best) best = score;
  }
  return best;
};

/**
 * Periodic resampling evidence plus the double-compression ringing hint.
 *
 * The strong term is 2× block repetition; the weak term is the case where the
 * quality stage sees ringing but little blockiness, which is what a second
 * re-encode of an already-compressed image tends to look like. The confidence
 * is capped at 0.5 on purpose: this is supporting evidence, never proof.
 */
export const resamplingSignal = (ctx: CheckRunContext): AuthenticitySignal => {
  const pixels = ctx.pixels;
  if (!pixels || pixels.width < 16 || pixels.height < 16) {
    return {
      id: 'resampling',
      score: 0,
      confidence: 0.02,
      note: 'Пиксельный буфер недоступен или слишком мал для оценки пересэмплинга.',
    };
  }
  const { luma, width, height } = pixels;

  let textureSum = 0;
  let textureCount = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x + 1 < width; x += 1) {
      textureSum += Math.abs((luma[y * width + x] as number) - (luma[y * width + x + 1] as number));
      textureCount += 1;
    }
  }
  const texture = textureCount > 0 ? textureSum / textureCount : 0;

  if (texture < 0.6) {
    return {
      id: 'resampling',
      score: 0,
      confidence: 0.03,
      note: 'Изображение слишком однородное для оценки пересэмплинга.',
    };
  }

  const repetition = blockRepetitionScore(luma, width, height);
  const compression = ctx.quality?.compression;
  const ringingHint = compression?.measured
    ? clamp01((compression.ringing - 0.3) / 0.4) * 0.55
    : 0;

  const score = clamp01(Math.max(repetition, ringingHint));
  const confidence = clamp01(
    0.3 + (width >= 200 && height >= 200 ? 0.15 : 0) + (repetition > 0.4 ? 0.15 : 0),
  );

  return {
    id: 'resampling',
    score,
    confidence,
    note:
      score >= 0.35
        ? 'периодическая структура пикселей, характерная для масштабирования'
        : 'следов пересэмплинга не видно',
  };
};

// ---------------------------------------------------------------------------
// H — clone / copy-paste
// ---------------------------------------------------------------------------

const CLONE_BLOCK = 16;
const CLONE_SUB = 4;
/** Blocks closer than this (in blocks) are never treated as a copy. */
const CLONE_MIN_OFFSET = 3;
/** How many block pairs must share one translation to call it copy-paste. */
const CLONE_MIN_PAIRS = 3;

interface BlockRef {
  x: number;
  y: number;
  values: number[];
}

/**
 * Coarse block hashing.
 *
 * The buffer is split into 16×16 blocks; each block is reduced to a 4×4 grid of
 * sub-means quantized to four levels. A block is only considered when it is
 * genuinely structured — enough contrast *and* variation along both axes — so
 * flat background patches and plain straight edges (which repeat by nature)
 * cannot match.
 *
 * A match must additionally be *pixel-close*: the quantized signature narrows
 * the candidates down, and the raw sub-means must then agree to within a couple
 * of luma levels. That rejects quantized coincidences (and JPEG block grids,
 * where many blocks share a coarse DC level) while a true copy-paste of the
 * same pixels passes with a distance of zero.
 *
 * Finally, one coincidental match is not enough: the same translation must be
 * seen by at least two block pairs forming a compact cluster.
 */
export const cloneSignal = (ctx: CheckRunContext): AuthenticitySignal => {
  const pixels = ctx.pixels;
  if (!pixels || pixels.width < CLONE_BLOCK * 3 || pixels.height < CLONE_BLOCK * 3) {
    return {
      id: 'clone',
      score: 0,
      confidence: 0.02,
      note: 'Пиксельный буфер слишком мал для поиска копированных фрагментов.',
    };
  }
  const { luma, width, height } = pixels;
  const cols = Math.floor(width / CLONE_BLOCK);
  const rows = Math.floor(height / CLONE_BLOCK);
  const cell = CLONE_BLOCK / CLONE_SUB;

  const groups = new Map<string, BlockRef[]>();
  let texturedBlocks = 0;

  for (let by = 0; by < rows; by += 1) {
    for (let bx = 0; bx < cols; bx += 1) {
      const values: number[] = [];
      for (let sy = 0; sy < CLONE_SUB; sy += 1) {
        for (let sx = 0; sx < CLONE_SUB; sx += 1) {
          let sum = 0;
          let count = 0;
          for (let y = by * CLONE_BLOCK + sy * cell; y < by * CLONE_BLOCK + (sy + 1) * cell; y += 1) {
            for (let x = bx * CLONE_BLOCK + sx * cell; x < bx * CLONE_BLOCK + (sx + 1) * cell; x += 1) {
              sum += luma[y * width + x] as number;
              count += 1;
            }
          }
          values.push(count > 0 ? sum / count : 0);
        }
      }
      const blockMean = mean(values);
      const blockSd = stdDev(values);
      let minimum = Number.POSITIVE_INFINITY;
      let maximum = Number.NEGATIVE_INFINITY;
      for (const value of values) {
        if (value < minimum) minimum = value;
        if (value > maximum) maximum = value;
      }
      // Flat blocks carry no structure to clone, and plain edges repeat by
      // nature along their whole length — both are excluded here.
      if (blockSd < 6 || maximum - minimum < 14) continue;

      const high = Math.max(2, blockSd * 0.5);
      const levels = values.map((value) =>
        value < blockMean - high ? 0 : value < blockMean ? 1 : value < blockMean + high ? 2 : 3,
      );
      let rowVaried = false;
      for (let r = 0; r < CLONE_SUB && !rowVaried; r += 1) {
        if (new Set(levels.slice(r * CLONE_SUB, (r + 1) * CLONE_SUB)).size >= 2) rowVaried = true;
      }
      let columnVaried = false;
      for (let c = 0; c < CLONE_SUB && !columnVaried; c += 1) {
        const column = [levels[c], levels[CLONE_SUB + c], levels[2 * CLONE_SUB + c], levels[3 * CLONE_SUB + c]];
        if (new Set(column).size >= 2) columnVaried = true;
      }
      if (!rowVaried || !columnVaried) continue;

      texturedBlocks += 1;
      const signature = levels.join('');
      const list = groups.get(signature);
      if (list) list.push({ x: bx, y: by, values });
      else groups.set(signature, [{ x: bx, y: by, values }]);
    }
  }

  if (texturedBlocks < 4) {
    return {
      id: 'clone',
      score: 0,
      confidence: 0.03,
      note: 'В кадре слишком мало структурированных участков для поиска копирования.',
    };
  }

  /** Maximum sub-mean difference still considered "the same pixels". */
  const CLONE_VALUE_TOLERANCE = 2;
  /** A group larger than this is a repeating texture, not a copied region. */
  const CLONE_MAX_GROUP = 32;

  const valuesClose = (a: BlockRef, b: BlockRef): boolean => {
    for (let index = 0; index < CLONE_SUB * CLONE_SUB; index += 1) {
      if (Math.abs((a.values[index] as number) - (b.values[index] as number)) > CLONE_VALUE_TOLERANCE) {
        return false;
      }
    }
    return true;
  };

  /*
   * A real copy-paste produces the same translation for a *compact* cluster of
   * blocks. A regular texture (a JPEG 8×8 grid, a repeating pattern) also
   * produces many same-offset matches, but they are spread over the whole
   * frame. Requiring the matched source blocks to stay within a small window
   * separates the two.
   */
  interface OffsetStat {
    pairs: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  }
  const MAX_CLUSTER_SPAN = 12;
  const offsets = new Map<string, OffsetStat>();
  for (const list of groups.values()) {
    // A group this large is a repeating texture (or a block grid), not a copy.
    if (list.length > CLONE_MAX_GROUP) continue;
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const a = list[i] as BlockRef;
        const b = list[j] as BlockRef;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        if (Math.max(Math.abs(dx), Math.abs(dy)) < CLONE_MIN_OFFSET) continue;
        if (!valuesClose(a, b)) continue;
        const key = `${dx}:${dy}`;
        const stat = offsets.get(key);
        if (stat) {
          stat.pairs += 1;
          stat.minX = Math.min(stat.minX, a.x);
          stat.maxX = Math.max(stat.maxX, a.x);
          stat.minY = Math.min(stat.minY, a.y);
          stat.maxY = Math.max(stat.maxY, a.y);
        } else {
          offsets.set(key, { pairs: 1, minX: a.x, maxX: a.x, minY: a.y, maxY: a.y });
        }
      }
    }
  }
  let bestPairs = 0;
  for (const stat of offsets.values()) {
    if (stat.maxX - stat.minX > MAX_CLUSTER_SPAN || stat.maxY - stat.minY > MAX_CLUSTER_SPAN) {
      continue;
    }
    if (stat.pairs > bestPairs) bestPairs = stat.pairs;
  }

  const score = bestPairs >= CLONE_MIN_PAIRS ? clamp01((bestPairs - 1) * 0.35) : 0;
  const confidence = clamp01(texturedBlocks >= 40 ? 0.6 : texturedBlocks >= 12 ? 0.45 : 0.3);

  return {
    id: 'clone',
    score,
    confidence,
    note:
      score >= 0.35
        ? 'найдены повторяющиеся фрагменты — возможна вставка/копирование'
        : 'повторяющихся фрагментов не найдено',
  };
};
